import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, MapPin, AlertTriangle, Stethoscope, HeartPulse, ShieldAlert, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';
export function EmergencyScreen() {
  const navigate = useNavigate();
  const [dispatchStatus, setDispatchStatus] = useState<'idle' | 'finding' | 'assigned'>('idle');
  const handleRequestHelp = () => {
    setDispatchStatus('finding');
    setTimeout(() => {
      setDispatchStatus('assigned');
    }, 3000);
  };
  return <div className="min-h-screen bg-[#FEF2F2] flex flex-col relative overflow-hidden" data-id="element-300">
      {/* Background Pulse */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-status-emergency/10 rounded-full blur-3xl animate-pulse" data-id="element-301"></div>

      <div className="pt-12 px-6 pb-4 relative z-10" data-id="element-302">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-red-100 transition-colors" data-id="element-303">
          <ArrowLeft size={24} className="text-status-emergency" data-id="element-304" />
        </button>
        <div className="flex items-center mt-2 mb-2" data-id="element-305">
          <AlertTriangle size={28} className="text-status-emergency mr-2" data-id="element-306" />
          <h1 className="text-3xl font-bold text-status-emergency" data-id="element-307">
            Emergency Help
          </h1>
        </div>
        <p className="text-red-800/80 font-medium" data-id="element-308">
          Get immediate medical assistance.
        </p>
      </div>

      <div className="flex-1 px-6 pb-8 relative z-10 flex flex-col" data-id="element-309">
        {/* Safety Notice */}
        <div className="bg-white border-l-4 border-status-emergency p-4 rounded-r-xl shadow-sm mb-8" data-id="element-310">
          <p className="text-sm font-bold text-gray-900" data-id="element-311">
            Life-threatening emergency?
          </p>
          <p className="text-xs text-gray-600 mt-1" data-id="element-312">
            Do not wait. Call local emergency services (112) immediately.
          </p>
        </div>

        {dispatchStatus === 'idle' ? <div className="space-y-4 animate-fade-in flex-1" data-id="element-313">
            <button className="w-full bg-status-emergency text-white p-5 rounded-2xl shadow-lg shadow-red-500/30 flex items-center justify-between active:scale-[0.98] transition-transform" data-id="element-314">
              <div className="flex items-center" data-id="element-315">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mr-4" data-id="element-316">
                  <Phone size={24} data-id="element-317" />
                </div>
                <div className="text-left" data-id="element-318">
                  <h3 className="font-bold text-lg" data-id="element-319">Call Emergency Line</h3>
                  <p className="text-white/80 text-sm" data-id="element-320">
                    Speak to a triage nurse now
                  </p>
                </div>
              </div>
            </button>

            <div className="grid grid-cols-2 gap-4 mt-6" data-id="element-321">
              <button onClick={handleRequestHelp} className="bg-white p-5 rounded-2xl border-2 border-red-100 shadow-sm flex flex-col items-center text-center active:bg-red-50 transition-colors" data-id="element-322">
                <div className="w-14 h-14 bg-red-100 text-status-emergency rounded-full flex items-center justify-center mb-3" data-id="element-323">
                  <Stethoscope size={28} data-id="element-324" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm" data-id="element-325">
                  Request Urgent Doctor
                </h3>
              </button>

              <button onClick={handleRequestHelp} className="bg-white p-5 rounded-2xl border-2 border-red-100 shadow-sm flex flex-col items-center text-center active:bg-red-50 transition-colors" data-id="element-326">
                <div className="w-14 h-14 bg-red-100 text-status-emergency rounded-full flex items-center justify-center mb-3" data-id="element-327">
                  <HeartPulse size={28} data-id="element-328" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm" data-id="element-329">
                  Request Urgent Nurse
                </h3>
              </button>
            </div>

            <div className="mt-6 bg-white p-4 rounded-2xl border border-red-100 shadow-sm flex items-center justify-between" data-id="element-330">
              <div className="flex items-center" data-id="element-331">
                <MapPin size={20} className="text-status-emergency mr-3" data-id="element-332" />
                <div data-id="element-333">
                  <p className="font-bold text-sm text-gray-900" data-id="element-334">
                    Share Live Location
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-335">
                    With emergency contacts
                  </p>
                </div>
              </div>
              <div className="w-12 h-6 bg-status-emergency rounded-full relative cursor-pointer" data-id="element-336">
                <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" data-id="element-337"></div>
              </div>
            </div>
          </div> : dispatchStatus === 'finding' ? <div className="flex-1 flex flex-col items-center justify-center animate-fade-in" data-id="element-338">
            <div className="relative w-32 h-32 flex items-center justify-center mb-8" data-id="element-339">
              <div className="absolute inset-0 border-4 border-status-emergency/20 rounded-full animate-ping" data-id="element-340"></div>
              <div className="absolute inset-4 border-4 border-status-emergency/40 rounded-full animate-ping" style={{
            animationDelay: '0.2s'
          }} data-id="element-341"></div>
              <div className="w-16 h-16 bg-status-emergency rounded-full flex items-center justify-center z-10 shadow-lg shadow-red-500/50" data-id="element-342">
                <ShieldAlert size={32} className="text-white" data-id="element-343" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-344">
              Finding nearest responder...
            </h2>
            <p className="text-gray-600 text-center px-6" data-id="element-345">
              We are locating the closest available medical professional to your
              location.
            </p>
          </div> : <div className="flex-1 flex flex-col animate-fade-in" data-id="element-346">
            <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-status-emergency relative overflow-hidden" data-id="element-347">
              <div className="absolute top-0 left-0 w-full h-1 bg-status-emergency" data-id="element-348"></div>

              <div className="flex justify-between items-start mb-6" data-id="element-349">
                <div data-id="element-350">
                  <span className="bg-red-100 text-status-emergency text-xs font-bold px-2 py-1 rounded mb-2 inline-block" data-id="element-351">
                    RESPONDER ASSIGNED
                  </span>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-352">
                    Dr. Tunde Bakare
                  </h2>
                  <p className="text-sm text-gray-600" data-id="element-353">Emergency Physician</p>
                </div>
                <img src="https://images.unsplash.com/photo-1537368910025-700350fe46c7?auto=format&fit=crop&q=80&w=100&h=100" alt="Responder" className="w-16 h-16 rounded-xl object-cover border border-gray-200" data-id="element-354" />
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-6" data-id="element-355">
                <div className="flex justify-between items-center mb-2" data-id="element-356">
                  <span className="text-sm text-gray-500" data-id="element-357">
                    Estimated Arrival
                  </span>
                  <span className="font-bold text-status-emergency text-lg" data-id="element-358">
                    8 mins
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden" data-id="element-359">
                  <motion.div initial={{
                width: '0%'
              }} animate={{
                width: '30%'
              }} className="bg-status-emergency h-full" data-id="element-360" />
                </div>
              </div>

              <div className="flex space-x-3" data-id="element-361">
                <button className="flex-1 bg-gray-100 text-gray-900 font-bold py-3 rounded-xl flex items-center justify-center" data-id="element-362">
                  <MessageCircle size={18} className="mr-2" data-id="element-363" /> Chat
                </button>
                <button className="flex-1 bg-status-emergency text-white font-bold py-3 rounded-xl flex items-center justify-center shadow-md" data-id="element-364">
                  <Phone size={18} className="mr-2" data-id="element-365" /> Call
                </button>
              </div>
            </div>

            <button onClick={() => setDispatchStatus('idle')} className="mt-8 text-gray-500 font-medium text-sm mx-auto" data-id="element-366">
              Cancel Request
            </button>
          </div>}
      </div>
    </div>;
}